import type * as zod from 'zod';

import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { StepFailure } from '@sb/routine-runner/types';
import { wait } from '@sb/utilities';

import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

const MIN_ITERATION_DURATION = 200;

export default class LoopStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = true;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public substeps: Array<Step<object, object>> = [];

  protected initializeVariableState(): void {
    const { currentIteration = 1 } = this.variablesForInitialization;

    const expectedTotalIterations = this.args.times ?? Infinity;

    this.variables = {
      currentIteration,
      expectedTotalIterations,
    };
  }

  public async _play({
    hasEncounteredStartStep,
    playSubSteps,
    hasBeenPreempted,
    fail,
  }: StepPlayArguments): Promise<void> {
    let isFirstIteration = true;

    while (true) {
      const timeAtStartOfIteration = Date.now();

      if (!isFirstIteration && hasEncounteredStartStep()) {
        this.iterate();
      }

      // Check loop condition to see if loop continues or should break
      const shouldContinueIteration = await this.shouldContinueIteration(fail);

      if (
        !shouldContinueIteration ||
        // RoutineRunner will "play" every step in a routine to search for the start step.
        // This is to support our "resume from" feature. When we're in this "soft play" mode, we
        // only want to iterate through this loop's sub steps once, then exit the loop. That way
        // sibling steps can then be "soft played" in order to find the start step.
        //
        // Example:
        // 1. Loop
        // 2.   Loop
        // 3.     Move
        // 4.     Wait
        // 5.   Loop
        // 6.     Move  <--- Resume from here
        // 7.     Wait
        //
        // If we resume from step 6, RoutineRunner will play step 1, which will play its substeps 2 and 5.
        // If we didn't break out of the loop here, then substep 2 would keep looping and we would never
        // get to substep 5.
        (!isFirstIteration && !hasEncounteredStartStep())
      ) {
        this.reset();
        break;
      }

      if (!isFirstIteration) {
        const routineRunnerState = this.routineContext.getRoutineRunnerState();

        if (
          routineRunnerState.kind === 'RoutineRunning' &&
          routineRunnerState.isPreflightTestRun &&
          this.routineContext.loadedRoutineState?.mainLoop === this.id
        ) {
          this.routineContext.setRoutineRunnerState({
            ...routineRunnerState,
            isPreflightTestRun: false,
          });
        }
      }

      isFirstIteration = false;

      if (await super.shouldStopPlaying()) {
        return;
      }

      // give the substeps an opportunity to break this loop
      // passed in through the recursive playSteps call
      let shouldBreak = false;
      let shouldContinue = false;

      await playSubSteps({
        breakOutOfLoop() {
          shouldBreak = true;
        },
        continueLoop() {
          shouldContinue = true;
        },
        hasBeenPreempted() {
          return shouldContinue || shouldBreak || hasBeenPreempted();
        },
      });

      if (hasBeenPreempted() || (await super.shouldStopPlaying())) {
        return;
      }

      if (shouldBreak) {
        break;
      }

      // prevent loop from block the event loop by waiting here
      const iterationDuration = Date.now() - timeAtStartOfIteration;

      const waitTime = Math.max(MIN_ITERATION_DURATION - iterationDuration, 0);

      await wait(waitTime);
    }
  }

  /**
   * Increment the loop at the beginning of each iteration.
   */
  private iterate(): void {
    this.setVariable('currentIteration', this.variables.currentIteration + 1);
  }

  /**
   * Increment the loop at the beginning of each iteration.
   */
  private reset(): void {
    this.setVariable('currentIteration', 1);
  }

  /**
   * Check whether the loop is valid with the current variable state.
   *
   * Basically the same as `next()` but doesn't actually modify state.
   *
   * Used when traversing through routine steps while trying to avoid modifying state.
   *
   * @returns Whether the loop should continue
   */
  private async shouldContinueIteration(
    onFail: (failure: StepFailure) => Promise<void>,
  ): Promise<boolean> {
    if (this.args.condition) {
      try {
        const result = await this.routineFrame.evaluateConditional(
          this.args.condition,
        );

        if (!result) {
          return false;
        }
      } catch (error) {
        await onFail({
          failure: {
            kind: FailureKind.InvalidRoutineLoadedFailure,
          },
          failureReason: error.message,
          error,
        });

        return false;
      }
    }

    const expectedTotalIterations = this.args.times ?? Infinity;

    // 1 based index, so check less than or equal to
    return this.variables.currentIteration <= expectedTotalIterations;
  }
}
